import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { X, Loader2, CalendarClock, Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight } from 'lucide-react'
import { addDays, addMonths, endOfMonth, endOfWeek, format, isBefore, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek, subMonths } from 'date-fns'
import { cs, de, enUS, es } from 'date-fns/locale'
import { BASE_SHAREPOINT_URL } from '../../../components/constants'
import { authHeader } from '../../../utils/authHeaders'
import { useTranslation } from '../../../i18n/LanguageContext'

// Lets an Admin/Super Admin pick a date+time (and optional "repeat every N
// days") for when every site configured on this task should sync itself,
// without anyone clicking "Sync all" by hand. Purely a thin CRUD UI over
// PUT/GET/DELETE /sharepoint/sync-schedule/{taskId} — the actual sync logic
// (which respects each site's selected folders) lives entirely server-side.

const DATE_FNS_LOCALES = { de, en: enUS, cs, es }

// Native <input type="date"/"time"> render as an OS-themed popup that can't
// take the aispace accent color, so date/time are picked through these two
// small popovers instead — same field values (date: 'yyyy-MM-dd', time: 'HH:mm').

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ref, onOutside])
}

const FieldButton = ({ icon: Icon, active, aispaceColor, placeholder, valueLabel, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-[14px] text-left transition-colors"
    style={{ borderColor: active ? aispaceColor : '#E5E7EB' }}
  >
    <span className={valueLabel ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{valueLabel || placeholder}</span>
    <Icon size={16} className="text-[#6B7280] flex-shrink-0" />
  </button>
)

const DatePickerField = ({ value, onChange, aispaceColor, locale, t }) => {
  const [open, setOpen] = useState(false)
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(value || new Date()))
  const ref = useRef(null)
  useClickOutside(ref, () => setOpen(false))

  useEffect(() => {
    if (open) setViewMonth(startOfMonth(value || new Date()))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const today = startOfDay(new Date())
  const gridStart = startOfWeek(startOfMonth(viewMonth))
  const gridEnd = endOfWeek(endOfMonth(viewMonth))
  const days = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d)
  const weekdayLabels = days.slice(0, 7).map((d) => format(d, 'EEEEE', { locale }))

  const selectDay = (day) => {
    onChange(format(day, 'yyyy-MM-dd'))
    setOpen(false)
  }

  return (
    <div className="relative" ref={ref}>
      <FieldButton
        icon={CalendarIcon}
        active={open}
        aispaceColor={aispaceColor}
        placeholder={t('agentSettings.filesScheduleSyncSelectDatePlaceholder')}
        valueLabel={value ? format(value, 'dd/MM/yyyy') : ''}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div className="absolute z-20 mt-1.5 w-[280px] bg-white rounded-xl shadow-xl border border-[#E5E7EB] p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[14px] font-semibold text-[#111827] capitalize">
              {format(viewMonth, 'LLLL yyyy', { locale })}
            </span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setViewMonth((m) => subMonths(m, 1))} className="p-1 rounded-md hover:bg-[#F3F4F6]">
                <ChevronLeft size={16} className="text-[#374151]" />
              </button>
              <button type="button" onClick={() => setViewMonth((m) => addMonths(m, 1))} className="p-1 rounded-md hover:bg-[#F3F4F6]">
                <ChevronRight size={16} className="text-[#374151]" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-y-1 text-center">
            {weekdayLabels.map((w, i) => (
              <span key={i} className="text-[11px] font-medium text-[#9CA3AF] py-1">{w}</span>
            ))}
            {days.map((day) => {
              const inMonth = isSameMonth(day, viewMonth)
              const past = isBefore(day, today)
              const selected = value && isSameDay(day, value)
              const isToday = isSameDay(day, today)
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  disabled={past}
                  onClick={() => selectDay(day)}
                  className="w-full aspect-square rounded-lg text-[13px] flex items-center justify-center transition-colors"
                  style={{
                    color: !inMonth || past ? '#D1D5DB' : selected ? '#FFFFFF' : '#111827',
                    backgroundColor: selected ? aispaceColor : 'transparent',
                    fontWeight: selected || isToday ? 600 : 400,
                    boxShadow: isToday && !selected ? `inset 0 0 0 1px ${aispaceColor}` : 'none',
                    cursor: past ? 'default' : 'pointer',
                  }}
                  onMouseEnter={(e) => { if (!selected && !past) e.currentTarget.style.backgroundColor = '#F3F4F6' }}
                  onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  {format(day, 'd')}
                </button>
              )
            })}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F3F4F6] px-1">
            <button type="button" onClick={() => { onChange(''); setOpen(false) }} className="text-[13px] font-medium" style={{ color: aispaceColor }}>
              {t('agentSettings.filesScheduleSyncClearButton')}
            </button>
            <button type="button" onClick={() => selectDay(new Date())} className="text-[13px] font-medium" style={{ color: aispaceColor }}>
              {t('agentSettings.filesScheduleSyncTodayButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1) // 1..12
const MINUTES = Array.from({ length: 60 }, (_, i) => i) // 0..59

const TimeColumn = ({ items, selected, onSelect, aispaceColor, format: fmt }) => {
  const itemRefs = useRef({})
  useEffect(() => {
    itemRefs.current[selected]?.scrollIntoView({ block: 'center' })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex-1 h-[200px] overflow-y-auto no-scrollbar">
      {items.map((item) => {
        const isSelected = item === selected
        return (
          <button
            key={item}
            ref={(el) => { itemRefs.current[item] = el }}
            type="button"
            onClick={() => onSelect(item)}
            className="w-full text-center py-1.5 text-[13px] rounded-md transition-colors"
            style={{
              backgroundColor: isSelected ? aispaceColor : 'transparent',
              color: isSelected ? '#FFFFFF' : '#374151',
              fontWeight: isSelected ? 600 : 400,
            }}
          >
            {fmt(item)}
          </button>
        )
      })}
    </div>
  )
}

const TimePickerField = ({ value, onChange, aispaceColor, t }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useClickOutside(ref, () => setOpen(false))

  const [h24, m] = value ? value.split(':').map(Number) : [null, null]
  const period = h24 === null ? null : h24 >= 12 ? 'PM' : 'AM'
  const hour12 = h24 === null ? null : ((h24 % 12) || 12)

  const commit = (nextHour12, nextMinute, nextPeriod) => {
    if (nextHour12 == null || nextMinute == null || !nextPeriod) return
    let next24 = nextHour12 % 12
    if (nextPeriod === 'PM') next24 += 12
    onChange(`${String(next24).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}`)
  }

  return (
    <div className="relative" ref={ref}>
      <FieldButton
        icon={Clock}
        active={open}
        aispaceColor={aispaceColor}
        placeholder={t('agentSettings.filesScheduleSyncSelectTimePlaceholder')}
        valueLabel={value ? `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}` : ''}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div className="absolute z-20 mt-1.5 w-[180px] bg-white rounded-xl shadow-xl border border-[#E5E7EB] p-2 flex gap-1">
          <TimeColumn
            items={HOURS_12}
            selected={hour12 ?? 12}
            onSelect={(v) => commit(v, m ?? 0, period ?? 'AM')}
            aispaceColor={aispaceColor}
            format={(v) => String(v).padStart(2, '0')}
          />
          <TimeColumn
            items={MINUTES}
            selected={m ?? 0}
            onSelect={(v) => commit(hour12 ?? 12, v, period ?? 'AM')}
            aispaceColor={aispaceColor}
            format={(v) => String(v).padStart(2, '0')}
          />
          <TimeColumn
            items={['AM', 'PM']}
            selected={period ?? 'AM'}
            onSelect={(v) => commit(hour12 ?? 12, m ?? 0, v)}
            aispaceColor={aispaceColor}
            format={(v) => v}
          />
        </div>
      )}
    </div>
  )
}

const SyncScheduleModal = ({ orgId, taskId, aispaceColor, t, onClose }) => {
  const { language } = useTranslation()
  const locale = DATE_FNS_LOCALES[language] || enUS
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [existing, setExisting] = useState(null)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [repeatOn, setRepeatOn] = useState(false)
  const [repeatDays, setRepeatDays] = useState(7)

  const dateValue = useMemo(() => (date ? new Date(`${date}T00:00:00`) : null), [date])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await axios.get(`${BASE_SHAREPOINT_URL}/sync-schedule/${taskId}`, { headers: { ...authHeader() } })
        if (cancelled) return
        const schedule = res.data?.schedule
        setExisting(schedule)
        if (schedule?.next_run_at) {
          // Local getters (via format()), not toISOString() — matches how handleSave/"Next sync" treat this as local time.
          const d = new Date(schedule.next_run_at + (schedule.next_run_at.endsWith('Z') ? '' : 'Z'))
          setDate(format(d, 'yyyy-MM-dd'))
          setTime(format(d, 'HH:mm'))
        }
        if (schedule?.repeat_every_days) {
          setRepeatOn(true)
          setRepeatDays(schedule.repeat_every_days)
        }
      } catch (e) {
        // No schedule yet is not an error state — form just starts empty.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [taskId])

  const handleSave = async () => {
    setError('')
    if (!date || !time) {
      setError(t('agentSettings.filesScheduleSyncPastDateError'))
      return
    }
    const localDateTime = new Date(`${date}T${time}:00`)
    if (localDateTime.getTime() <= Date.now()) {
      setError(t('agentSettings.filesScheduleSyncPastDateError'))
      return
    }
    setSaving(true)
    try {
      await axios.put(
        `${BASE_SHAREPOINT_URL}/sync-schedule/${taskId}`,
        {
          org_id: orgId,
          next_run_at: localDateTime.toISOString(),
          repeat_every_days: repeatOn ? Number(repeatDays) : null,
        },
        { headers: { ...authHeader() } }
      )
      onClose(true)
    } catch (e) {
      setError(e.response?.data?.detail || e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async () => {
    setSaving(true)
    try {
      await axios.delete(`${BASE_SHAREPOINT_URL}/sync-schedule/${taskId}`, { headers: { ...authHeader() } })
      onClose(true)
    } catch (e) {
      setError(e.response?.data?.detail || e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-[440px] shadow-xl overflow-visible flex flex-col">
        <div className="flex items-start justify-between px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${aispaceColor}1A` }}>
              <CalendarClock size={18} style={{ color: aispaceColor }} />
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-[#111827]">{t('agentSettings.filesScheduleSyncTitle')}</h2>
              <p className="text-[13px] text-[#6B7280] mt-0.5">{t('agentSettings.filesScheduleSyncSubtitle')}</p>
            </div>
          </div>
          <button onClick={() => onClose(false)} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
          </div>
        ) : (
          <div className="px-6 pb-6 space-y-4">
            {existing?.next_run_at && (
              <p className="text-[13px] text-[#6B7280]">
                {t('agentSettings.filesScheduleSyncNextRunLabel')}{' '}
                <span className="font-medium text-[#111827]">{new Date(existing.next_run_at + (existing.next_run_at.endsWith('Z') ? '' : 'Z')).toLocaleString()}</span>
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1.5">{t('agentSettings.filesScheduleSyncDateLabel')}</label>
                <DatePickerField value={dateValue} onChange={setDate} aispaceColor={aispaceColor} locale={locale} t={t} />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1.5">{t('agentSettings.filesScheduleSyncTimeLabel')}</label>
                <TimePickerField value={time} onChange={setTime} aispaceColor={aispaceColor} t={t} />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={repeatOn}
                onChange={(e) => setRepeatOn(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                style={{ accentColor: aispaceColor }}
              />
              <span className="text-[13px] text-[#374151]">{t('agentSettings.filesScheduleSyncRepeatLabel')}</span>
            </label>

            {repeatOn && (
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1.5">{t('agentSettings.filesScheduleSyncRepeatDaysLabel')}</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={repeatDays}
                  onChange={(e) => setRepeatDays(e.target.value)}
                  className="w-24 border border-[#E5E7EB] rounded-lg px-3 py-2 text-[14px] focus:outline-none"
                  onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
                  onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                />
              </div>
            )}

            {error && <p className="text-[13px] text-red-600">{error}</p>}

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
                style={{ backgroundColor: aispaceColor }}
              >
                {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : t('agentSettings.filesScheduleSyncSaveButton')}
              </button>
              {existing && (
                <button
                  onClick={handleRemove}
                  disabled={saving}
                  className="px-4 py-2.5 text-[14px] font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {t('agentSettings.filesScheduleSyncRemoveButton')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SyncScheduleModal
